import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Card } from '@/components/ui';
import { useAdminPermission } from '@/lib/useAdminPermission';

/**
 * ForceLogoutControl — a per-user "sign out everywhere" action shared by
 * CustomerDrawer and DriverDrawer. The write posts to admin-svc, which proxies
 * auth-svc token revocation: every active session for the user dies, but the
 * account status is UNCHANGED, so they can log straight back in. Useful for a
 * lost/stolen device or a stale session without the heavier block/ban.
 *
 * RBAC: gated on the SAME permission BlockBanControl uses (customers.block for
 * the customer scope, drivers.ban for the driver scope) — only ops + superadmin
 * hold those, so other roles don't see the control. (admin-svc enforces the same
 * matrix and 403s regardless — this is a UX affordance, not the security
 * boundary.)
 *
 * On success it invalidates the parent detail query (['admin','customer'|'driver',id])
 * so the drawer stays consistent.
 */
export function ForceLogoutControl({ scope, id }: { scope: 'customer' | 'driver'; id: number }) {
  // Same gate as BlockBanControl: customers.block / drivers.ban.
  const perm = scope === 'customer' ? 'customers.block' : 'drivers.ban';
  const canAct = useAdminPermission(perm);
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      scope === 'customer' ? adminApi.forceLogoutCustomer(id) : adminApi.forceLogoutDriver(id),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['admin', scope, id] });
    },
  });

  // Hide entirely for roles without the capability (server enforces too).
  if (!canAct) return null;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-100">Force logout</div>
          <p className="mt-1 text-xs text-muted">
            Signs this {scope} out of all devices (every active session is revoked). The account stays
            active and they can log back in immediately. Audited.
          </p>
        </div>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            setConfirming((v) => !v);
            mutation.reset();
          }}
          className="shrink-0 rounded-md bg-warn/15 px-2.5 py-1 text-xs font-medium text-warn ring-1 ring-inset ring-warn/30 hover:bg-warn/25 disabled:opacity-40"
        >
          Force logout
        </button>
      </div>

      {confirming ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="min-w-0 flex-1 text-[11px] text-text-secondary">
            Sign this user out of all devices? They can log back in immediately.
          </p>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="shrink-0 rounded-md bg-warn px-3 py-1.5 text-xs font-semibold text-white hover:bg-warn/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Working…' : 'Confirm logout'}
          </button>
        </div>
      ) : null}

      {mutation.isSuccess ? <p className="mt-2 text-xs text-ok">{mutation.data?.message}</p> : null}
      {mutation.isError ? <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
    </Card>
  );
}
